import { useState, useEffect } from "react";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, STAGES } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EngagementCreateModal from "@/components/EngagementCreateModal";
import { useClientDrawer } from "@/components/ClientDrawerProvider";
import {
  Plus,
  GripVertical,
  TrendingUp,
  TrendingDown,
  Activity,
  AlertTriangle,
  Target,
  Users,
  Flame,
  Snowflake,
  Clock,
  CheckCircle,
  XCircle,
  ArrowRight,
  Loader2,
} from "lucide-react";

// Relationship-based pipeline categories
const RELATIONSHIP_CATEGORIES = [
  {
    id: "hot",
    label: "Hot Opportunities",
    description: "Rising momentum + Decision/Commitment readiness",
    icon: Flame,
    color: "bg-orange-500",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    textColor: "text-orange-700",
    filter: (rs) =>
      rs?.momentum === "rising" &&
      ["decision", "commitment"].includes(rs?.readiness),
  },
  {
    id: "progressing",
    label: "Progressing",
    description: "Active engagement + Alignment/Decision",
    icon: TrendingUp,
    color: "bg-green-500",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    textColor: "text-green-700",
    filter: (rs) =>
      ["rising", "active"].includes(rs?.momentum) &&
      ["alignment", "decision"].includes(rs?.readiness),
  },
  {
    id: "nurturing",
    label: "Nurturing",
    description: "Active/Paused + Awareness/Exploration",
    icon: Activity,
    color: "bg-blue-500",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
    filter: (rs) =>
      ["active", "paused", "rising"].includes(rs?.momentum) &&
      ["awareness", "exploration"].includes(rs?.readiness),
  },
  {
    id: "at_risk",
    label: "At Risk",
    description: "Cooling momentum - needs attention",
    icon: AlertTriangle,
    color: "bg-amber-500",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    textColor: "text-amber-700",
    filter: (rs) => rs?.momentum === "cooling",
  },
  {
    id: "dormant",
    label: "Dormant",
    description: "No engagement in 30+ days",
    icon: Snowflake,
    color: "bg-slate-400",
    bgColor: "bg-slate-50",
    borderColor: "border-slate-200",
    textColor: "text-slate-600",
    filter: (rs) => rs?.momentum === "dormant",
  },
  {
    id: "new",
    label: "New / Untracked",
    description: "No relationship data yet",
    icon: Clock,
    color: "bg-purple-500",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-700",
    filter: (rs) => !rs || rs?.strength === "unknown",
  },
];

export default function Pipeline({ user }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [draggedClient, setDraggedClient] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null); // category id or stage id
  const [viewMode, setViewMode] = useState("relationship");
  const [workspaceId, setWorkspaceId] = useState(null);
  const [wonHandshake, setWonHandshake] = useState(null); // { client_id, client_name, opportunity_id }
  const navigate = useNavigate();
  const { openClient } = useClientDrawer();

  useEffect(() => {
    fetchClients();
    // Load v2 workspace for closed-won handshake (deduped singleton)
    fetchMyWorkspaces()
      .then((rows) => rows?.[0] && setWorkspaceId(rows[0].workspace_id))
      .catch(() => {});
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/clients`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setClients(Array.isArray(data) ? data : []);
        setLoadError(false);
      } else {
        // A failed request is NOT an empty board — say so and let the user retry.
        setLoadError(true);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const getClientsByStage = (stageId) => {
    return clients.filter((c) => c.stage === stageId);
  };

  const getClientsByRelationshipCategory = (category) => {
    return clients.filter((c) => category.filter(c.relationship_state));
  };

  // --- Unified drag handlers ---
  const handleDragStart = (e, client) => {
    setDraggedClient(client);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", client.client_id);
    // Make the drag image slightly transparent
    if (e.target) {
      e.target.style.opacity = "0.5";
      setTimeout(() => { if (e.target) e.target.style.opacity = "1"; }, 0);
    }
  };

  const handleDragOver = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    setDragOverTarget(targetId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverTarget(null);
  };

  const handleDragEnd = () => {
    setDraggedClient(null);
    setDragOverTarget(null);
  };

  // Drop handler for RELATIONSHIP categories
  const handleRelationshipDrop = async (e, categoryId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);

    if (!draggedClient) return;

    // Check if already in this category
    const category = RELATIONSHIP_CATEGORIES.find(c => c.id === categoryId);
    if (category && category.filter(draggedClient.relationship_state)) {
      setDraggedClient(null);
      return;
    }

    const clientId = draggedClient.client_id;
    const clientName = draggedClient.company_name;
    const previousState = { ...draggedClient.relationship_state };

    // Optimistic update: set relationship_state based on category defaults
    const CATEGORY_DEFAULTS = {
      hot: { momentum: "rising", readiness: "decision" },
      progressing: { momentum: "active", readiness: "alignment" },
      nurturing: { momentum: "active", readiness: "exploration" },
      at_risk: { momentum: "cooling" },
      dormant: { momentum: "dormant" },
      new: { momentum: null, readiness: null, strength: "unknown" },
    };
    const defaults = CATEGORY_DEFAULTS[categoryId] || {};

    setClients(prev => prev.map(c => {
      if (c.client_id !== clientId) return c;
      const rs = { ...(c.relationship_state || {}) };
      if (categoryId === "new") {
        rs.momentum = null;
        rs.readiness = null;
        rs.strength = "unknown";
      } else {
        Object.assign(rs, defaults);
        if (rs.strength === "unknown") rs.strength = "emerging";
      }
      return { ...c, relationship_state: rs };
    }));

    try {
      const response = await fetch(`${API}/clients/${clientId}/pipeline-move`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_category: categoryId }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClients(prev => prev.map(c => c.client_id === clientId ? updated : c));
        toast.success(`Moved ${clientName} to ${category?.label || categoryId}`);
      } else {
        // Revert
        setClients(prev => prev.map(c =>
          c.client_id === clientId ? { ...c, relationship_state: previousState } : c
        ));
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to move client");
      }
    } catch {
      setClients(prev => prev.map(c =>
        c.client_id === clientId ? { ...c, relationship_state: previousState } : c
      ));
      toast.error("Failed to move client");
    }

    setDraggedClient(null);
  };

  // Drop handler for TRADITIONAL stages
  const handleStageDrop = async (e, stageId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverTarget(null);

    if (!draggedClient || draggedClient.stage === stageId) {
      setDraggedClient(null);
      return;
    }

    const previousStage = draggedClient.stage;
    const clientId = draggedClient.client_id;
    const clientName = draggedClient.company_name;

    // Optimistic update
    setClients(prev => prev.map(c =>
      c.client_id === clientId ? { ...c, stage: stageId } : c
    ));

    try {
      const response = await fetch(`${API}/clients/${clientId}/pipeline-move`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ target_stage: stageId }),
      });

      if (response.ok) {
        const updated = await response.json();
        setClients(prev => prev.map(c => c.client_id === clientId ? updated : c));
        toast.success(`Moved ${clientName} to ${stageId}`);
        // REX v3 — closed-won handshake
        if (stageId === "Closed \u2013 Won") {
          setWonHandshake({
            client_id: clientId,
            client_name: clientName,
            opportunity_id: updated?.opportunity_id || null,
          });
        }
      } else {
        setClients(prev => prev.map(c =>
          c.client_id === clientId ? { ...c, stage: previousStage } : c
        ));
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update client stage");
      }
    } catch {
      setClients(prev => prev.map(c =>
        c.client_id === clientId ? { ...c, stage: previousStage } : c
      ));
      toast.error("Failed to update client stage");
    }

    setDraggedClient(null);
  };

  // Calculate pipeline health stats
  const pipelineStats = {
    hot: getClientsByRelationshipCategory(RELATIONSHIP_CATEGORIES[0]).length,
    progressing: getClientsByRelationshipCategory(RELATIONSHIP_CATEGORIES[1]).length,
    atRisk: getClientsByRelationshipCategory(RELATIONSHIP_CATEGORIES[3]).length,
    dormant: getClientsByRelationshipCategory(RELATIONSHIP_CATEGORIES[4]).length,
    total: clients.length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Item 8 — a failed load must not masquerade as an empty pipeline.
  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center" data-testid="pipeline-load-error">
        <AlertTriangle className="w-8 h-8 text-rose-500" />
        <div>
          <p className="font-semibold text-slate-800">We couldn't load your pipeline</p>
          <p className="text-sm text-slate-500 mt-1">The request to load clients failed — this is not an empty result. Please try again.</p>
        </div>
        <Button onClick={fetchClients} data-testid="pipeline-retry-btn" className="bg-blue-600 hover:bg-blue-700">
          Retry
        </Button>
      </div>
    );
  }

  // Item 9 — say WHY categories are empty when the data is simply absent.
  const noRelData = clients.filter((c) => RELATIONSHIP_CATEGORIES[5].filter(c.relationship_state)).length;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="pipeline-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']">
            Sales Pipeline
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {viewMode === "relationship"
              ? "Drag clients between categories to update their relationship state"
              : "Drag and drop clients between stages"}
          </p>
          {clients.length > 0 && noRelData > 0 && (
            <p className="text-xs text-amber-700 mt-1" data-testid="pipeline-coverage-line">
              {noRelData.toLocaleString()} of {clients.length.toLocaleString()} clients have no relationship data yet — categories look empty because that data is absent, not because there are no clients.
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link to="/clients">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              size="sm"
              data-testid="add-client-pipeline-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Client
            </Button>
          </Link>
        </div>
      </div>

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode} className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger
            value="relationship"
            className="data-[state=active]:bg-white"
            data-testid="tab-relationship-view"
          >
            <Activity className="w-4 h-4 mr-2" />
            Relationship Intelligence
          </TabsTrigger>
          <TabsTrigger
            value="traditional"
            className="data-[state=active]:bg-white"
            data-testid="tab-traditional-view"
          >
            <Target className="w-4 h-4 mr-2" />
            Traditional Stages
          </TabsTrigger>
        </TabsList>

        {/* Relationship Intelligence View */}
        <TabsContent value="relationship" className="space-y-6">
          {/* Pipeline Health Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-500 rounded-lg">
                    <Flame className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-700">
                      {pipelineStats.hot}
                    </p>
                    <p className="text-xs text-orange-600">Hot Opportunities</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-700">
                      {pipelineStats.progressing}
                    </p>
                    <p className="text-xs text-green-600">Progressing</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">
                      {pipelineStats.atRisk}
                    </p>
                    <p className="text-xs text-amber-600">At Risk</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-400 rounded-lg">
                    <Snowflake className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-700">
                      {pipelineStats.dormant}
                    </p>
                    <p className="text-xs text-slate-600">Dormant</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Relationship-Based Pipeline Columns */}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {RELATIONSHIP_CATEGORIES.map((category) => {
                const categoryClients = getClientsByRelationshipCategory(category);
                const Icon = category.icon;
                const isDropTarget = dragOverTarget === category.id &&
                  draggedClient && !category.filter(draggedClient.relationship_state);

                return (
                  <div
                    key={category.id}
                    className={`w-80 flex-shrink-0 transition-all duration-200 ${isDropTarget ? "scale-[1.02]" : ""}`}
                    onDragOver={(e) => handleDragOver(e, category.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleRelationshipDrop(e, category.id)}
                    data-testid={`category-column-${category.id}`}
                  >
                    <div
                      className={`${category.bgColor} ${category.borderColor} border rounded-lg p-3 min-h-[400px] transition-all duration-200 ${
                        isDropTarget ? "ring-2 ring-blue-400 shadow-lg" : ""
                      }`}
                    >
                      {/* Category Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 ${category.color} rounded`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <span className={`font-semibold ${category.textColor}`}>
                              {category.label}
                            </span>
                            <p className="text-xs text-slate-500">
                              {category.description}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant="secondary"
                          className={`${category.bgColor} ${category.textColor} border ${category.borderColor}`}
                        >
                          {categoryClients.length}
                        </Badge>
                      </div>

                      {/* Client Cards */}
                      <div className="space-y-2 min-h-[300px]">
                        {categoryClients.map((client) => (
                          <RelationshipClientCard
                            key={client.client_id}
                            client={client}
                            category={category}
                            isDragging={draggedClient?.client_id === client.client_id}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                          />
                        ))}

                        {categoryClients.length === 0 && (
                          <div className={`flex items-center justify-center h-24 border-2 border-dashed rounded-lg transition-colors ${
                            isDropTarget ? "border-blue-400 bg-blue-100/50" : "border-slate-200 bg-white/50"
                          }`}>
                            <p className={`text-sm ${isDropTarget ? "text-blue-600 font-medium" : "text-slate-400"}`}>
                              {isDropTarget ? "Release to drop here" : "No clients in this category"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        {/* Traditional Stage View */}
        <TabsContent value="traditional">
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-4 min-w-max">
              {STAGES.map((stage) => {
                const stageClients = getClientsByStage(stage.id);
                const isDropTarget =
                  dragOverTarget === stage.id &&
                  draggedClient?.stage !== stage.id;
                return (
                  <div
                    key={stage.id}
                    className={`w-72 flex-shrink-0 transition-all duration-200 ${
                      isDropTarget ? "scale-[1.02]" : ""
                    }`}
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleStageDrop(e, stage.id)}
                    data-testid={`stage-column-${stage.id
                      .toLowerCase()
                      .replace(" ", "-")}`}
                  >
                    <div
                      className={`bg-slate-100 rounded-lg p-3 min-h-[400px] transition-all duration-200 ${
                        isDropTarget ? "ring-2 ring-blue-400 bg-blue-50" : ""
                      }`}
                    >
                      {/* Stage Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                          <span className="font-medium text-slate-700">
                            {stage.label}
                          </span>
                        </div>
                        <Badge variant="secondary" className="bg-white">
                          {stageClients.length}
                        </Badge>
                      </div>

                      {/* Cards */}
                      <div className="space-y-2 min-h-[300px]">
                        {stageClients.map((client) => (
                          <Card
                            key={client.client_id}
                            className={`cursor-grab active:cursor-grabbing hover:shadow-md transition-all duration-200 border-slate-200 ${
                              draggedClient?.client_id === client.client_id
                                ? "opacity-50 scale-95"
                                : ""
                            }`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, client)}
                            onDragEnd={handleDragEnd}
                          >
                            <CardContent className="p-3">
                              <div className="flex items-start gap-2">
                                <GripVertical className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openClient(client.client_id); }}
                                    className="font-medium text-slate-900 hover:text-blue-600 block truncate text-left"
                                    data-testid={`pipeline-client-${client.client_id}`}
                                  >
                                    {client.company_name}
                                  </button>
                                  <p className="text-xs text-slate-500 mt-1 truncate">
                                    {client.sector || "No sector"}
                                  </p>
                                  <div className="flex items-center gap-2 mt-2">
                                    {client.tier && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs px-1.5 py-0"
                                      >
                                        {client.tier}
                                      </Badge>
                                    )}
                                    {client.relationship_state?.momentum && (
                                      <Badge
                                        variant="outline"
                                        className={`text-xs px-1.5 py-0 ${
                                          client.relationship_state.momentum ===
                                          "rising"
                                            ? "border-green-500 text-green-600"
                                            : client.relationship_state
                                                .momentum === "cooling"
                                            ? "border-amber-500 text-amber-600"
                                            : client.relationship_state
                                                .momentum === "dormant"
                                            ? "border-red-500 text-red-600"
                                            : "border-slate-300 text-slate-500"
                                        }`}
                                      >
                                        {client.relationship_state.momentum}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}

                        {stageClients.length === 0 && (
                          <div
                            className={`flex items-center justify-center h-24 border-2 border-dashed rounded-lg transition-colors ${
                              isDropTarget
                                ? "border-blue-400 bg-blue-100"
                                : "border-slate-200"
                            }`}
                          >
                            <p
                              className={`text-sm ${
                                isDropTarget
                                  ? "text-blue-600 font-medium"
                                  : "text-slate-400"
                              }`}
                            >
                              {isDropTarget
                                ? "Release to drop here"
                                : "Drop clients here"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* REX v3 — Closed-Won Handshake */}
      <EngagementCreateModal
        open={!!wonHandshake}
        onClose={() => setWonHandshake(null)}
        workspaceId={workspaceId}
        defaultClientId={wonHandshake?.client_id}
        defaultClientName={wonHandshake?.client_name}
        defaultName={wonHandshake?.client_name ? `${wonHandshake.client_name} — Engagement` : ""}
        sourceDealId={wonHandshake?.opportunity_id}
        onCreated={(eng) => {
          setWonHandshake(null);
          if (eng?.engagement_id) {
            navigate(`/engagements?focus=${eng.engagement_id}`);
          }
        }}
      />
    </div>
  );
}

// Relationship-based client card component
function RelationshipClientCard({ client, category, isDragging, onDragStart, onDragEnd }) {
  const rs = client.relationship_state || {};
  const hasStageMismatch = rs.stage_mismatch;
  const { openClient } = useClientDrawer();

  return (
    <Card
      className={`hover:shadow-md transition-all duration-200 border-slate-200 bg-white cursor-grab active:cursor-grabbing ${
        isDragging ? "opacity-40 scale-95 ring-2 ring-blue-400" : ""
      }`}
      draggable
      onDragStart={(e) => onDragStart(e, client)}
      onDragEnd={onDragEnd}
      data-testid={`pipeline-card-${client.client_id}`}
    >
      <CardContent className="p-3">
        <div className="space-y-2">
          {/* Company Name & Link */}
          <div className="flex items-start justify-between gap-1">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openClient(client.client_id); }}
                className="font-medium text-slate-900 hover:text-blue-600 block truncate text-left"
                data-testid={`pipeline-rcard-client-${client.client_id}`}
              >
                {client.company_name}
              </button>
            </div>
            {hasStageMismatch && (
              <span title="Stage mismatch — Rex's relationship analysis differs from the manual CRM stage">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              </span>
            )}
          </div>

          {/* Sector */}
          <p className="text-xs text-slate-500 truncate">
            {client.sector || "No sector"}
          </p>

          {/* Relationship State Badges */}
          <div className="flex flex-wrap gap-1">
            {rs.strength && rs.strength !== "unknown" && (
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0 bg-purple-50 border-purple-200 text-purple-700 capitalize"
              >
                {rs.strength}
              </Badge>
            )}
            {rs.readiness && rs.readiness !== "awareness" && (
              <Badge
                variant="outline"
                className="text-xs px-1.5 py-0 bg-amber-50 border-amber-200 text-amber-700 capitalize"
              >
                {rs.readiness}
              </Badge>
            )}
          </div>

          {/* Stakeholders */}
          {rs.stakeholders?.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-slate-500">
              <Users className="w-3 h-3" />
              <span>{rs.stakeholders.length} stakeholder(s)</span>
            </div>
          )}

          {/* Stage Comparison */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-400">Manual:</span>
            <Badge variant="outline" className="text-xs px-1.5 py-0">
              {client.stage || "Lead"}
            </Badge>
            {rs.derived_stage && rs.derived_stage !== client.stage && (
              <>
                <ArrowRight className="w-3 h-3 text-slate-400" />
                <Badge
                  variant="outline"
                  className="text-xs px-1.5 py-0 bg-blue-50 border-blue-200 text-blue-700"
                >
                  {rs.derived_stage}
                </Badge>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
